import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ModalStyled } from '../../../theme/customizedStyleComponents';
import { AgGridReact } from 'ag-grid-react';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { serviceConstants } from '../../../services/common/constants';
import { getUserAuthToken } from '../../../services/common/util';
import { fetchResultHistory } from '../../../services/clinicPortalServices/orderEditService';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class TestResultStatusRenderer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasResults: false,
            resultStatus: '',
            statusColor: '',
            pdfPath: '',
            showPopup: false,
            quickFilterText: '',
            rowData: [],
            showHistory: false,
            historyRows: [],
            historyLoading: false,
            abnormalCount: 0,
            inconsistentCount: 0,
            totalCount: 0,
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true
            },
            columnDefs: [
                {
                    headerName: 'Analyte',
                    minWidth: 200,
                    field: 'analyteName'
                },
                {
                    headerName: 'Result',
                    minWidth: 100,
                    field: 'analyteResult',
                    cellStyle: (params) => {
                        const flag = params.data.analyteFlag;
                        if (flag === 'H' || flag === 'L' || flag === 'A') {
                            return { color: '#dc3545', fontWeight: 'bold' };
                        }
                        return null;
                    }
                },
                {
                    headerName: 'Units',
                    minWidth: 100,
                    field: 'analyteUnits'
                },
                {
                    headerName: 'Reference Range',
                    minWidth: 150,
                    field: 'analyteReferenceRange'
                },
                {
                    headerName: 'Flag',
                    minWidth: 80,
                    field: 'analyteFlag',
                    cellStyle: (params) => {
                        const flagColor = params.data.flagColor;
                        if (flagColor) {
                            return {
                                color: `#${flagColor}`,
                                fontWeight: 'bold'
                            };
                        }
                        // Legacy flag handling
                        const flag = params.value;
                        if (flag === 'H') return { color: '#dc3545', fontWeight: 'bold' };
                        if (flag === 'L') return { color: '#0066cc', fontWeight: 'bold' };
                        if (flag === 'A') return { color: '#ff6600', fontWeight: 'bold' };
                        return null;
                    },
                    valueFormatter: (params) => {
                        const flag = params.value;
                        // Show flag name as-is for new flags (DET, ND, BLOQ, etc.)
                        if (flag && !['H', 'L', 'A'].includes(flag)) {
                            return flag;
                        }
                        // Legacy formatting
                        if (flag === 'H') return 'High';
                        if (flag === 'L') return 'Low';
                        if (flag === 'A') return 'Abnormal';
                        return flag || '';
                    }
                },
                {
                    headerName: 'Outcome',
                    minWidth: 120,
                    field: 'flagOutcome',
                    cellStyle: (params) => {
                        const outcome = params.value;
                        if (outcome === 'Consistent') {
                            return {
                                color: '#28a745',
                                fontWeight: 'bold'
                            };
                        } else if (outcome === 'Inconsistent') {
                            return {
                                color: '#dc3545',
                                fontWeight: 'bold'
                            };
                        }
                        return null;
                    },
                    valueFormatter: (params) => {
                        return params.value || '';
                    }
                }
            ],
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            }
        };
    }

    componentDidMount() {
        this.updateResultStatus();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this.updateResultStatus();
        }
    }

    updateResultStatus = () => {
        const { data } = this.props;

        if (!data || !data.results) {
            this.setState({
                hasResults: false,
                resultStatus: 'Pending',
                statusColor: '#666',
                pdfPath: '',
                rowData: [],
                showHistory: false,
                historyRows: []
            });
            return;
        }

        // Check if order is cancelled
        if (data.is_cancel) {
            this.setState({
                hasResults: false,
                resultStatus: 'Cancelled',
                statusColor: '#dc3545',
                pdfPath: '',
                rowData: [],
                showHistory: false,
                historyRows: []
            });
            return;
        }

        let pdfPath = '';
        if (data.results.pdf_path) {
            pdfPath = `${serviceConstants.API_HOST_NAME}${
                data.results.pdf_path
            }?${new Date().getTime()}&token=${getUserAuthToken()}`;
        }

        // Check for qualitative test (single result value)
        if (data.results.value) {
            const value = data.results.value.toLowerCase();
            let statusColor = '#28a745'; // green by default

            if (value.includes('positive') || value.includes('detected')) {
                statusColor = '#dc3545'; // red for positive
            } else if (value.includes('invalid') || value.includes('inconclusive')) {
                statusColor = '#ffc107'; // yellow for invalid/inconclusive
            }

            this.setState({
                hasResults: true,
                resultStatus: data.results.value,
                statusColor: statusColor,
                pdfPath: pdfPath,
                rowData: [
                    {
                        analyteName: data.test_id?.name || 'Test Result',
                        analyteResult: data.results.value,
                        analyteUnits: '',
                        analyteReferenceRange: '',
                        analyteFlag: ''
                    }
                ],
                showHistory: value.includes('detected'),
                historyRows: []
            });
            return;
        }

        // Check for quantitative test (multiple analyte values)
        if (data.results.values && data.results.values.length > 0) {
            const totalCount = data.results.values.length;

            // Count flags and outcomes
            const flaggedCount = data.results.values.filter((v) => v.analyteFlag).length;
            const inconsistentCount = data.results.values.filter((v) => v.flagOutcome === 'Inconsistent').length;

            // Legacy abnormal count
            const abnormalCount = data.results.values.filter(
                (v) => v.analyteFlag && (v.analyteFlag === 'H' || v.analyteFlag === 'L' || v.analyteFlag === 'A')
            ).length;

            let statusText = `${totalCount} analyte${totalCount > 1 ? 's' : ''} complete`;
            let statusColor = '#28a745'; // green

            // Prioritize inconsistent results
            if (inconsistentCount > 0) {
                statusText = `Complete - ${inconsistentCount} inconsistent`;
                statusColor = '#dc3545'; // red for inconsistent
            } else if (abnormalCount > 0) {
                statusText = `Complete - ${abnormalCount} abnormal`;
                statusColor = '#dc3545'; // red for abnormal
            } else if (flaggedCount > 0) {
                statusText = `Complete - ${flaggedCount} flagged`;
                statusColor = '#ffc107'; // yellow for flagged but consistent
            }

            // Show History when any analyte is Detected (DET flag or "detected" result)
            // or has an Inconsistent outcome.
            const hasHistoryTrigger = data.results.values.some(
                (v) =>
                    v.analyteFlag === 'DET' ||
                    v.analyteFlag === 'det' ||
                    (v.analyteResult && v.analyteResult.toString().toLowerCase().includes('detected')) ||
                    (v.flagOutcome && v.flagOutcome.toString().toLowerCase() === 'inconsistent')
            );

            this.setState({
                hasResults: true,
                resultStatus: statusText,
                statusColor: statusColor,
                pdfPath: pdfPath,
                rowData: data.results.values,
                abnormalCount: abnormalCount,
                inconsistentCount: inconsistentCount,
                totalCount: totalCount,
                showHistory: hasHistoryTrigger,
                historyRows: []
            });
            return;
        }

        // No results yet
        this.setState({
            hasResults: false,
            resultStatus: 'Pending',
            statusColor: '#666',
            pdfPath: '',
            rowData: [],
            showHistory: false,
            historyRows: []
        });
    };

    handleClose = () => {
        this.setState({ showPopup: false });
    };

    handleClick = () => {
        if (this.state.hasResults && this.state.rowData.length > 0) {
            this.setState({ showPopup: true });
            if (this.state.showHistory) {
                this.loadHistory();
            }
        }
    };

    loadHistory = () => {
        const { data } = this.props;
        const patientId = data?.patient_id?._id;
        const testId = data?.test_id?._id;
        // Need both patient and test to look up history; skip legacy orders without a test_id
        if (!patientId || !testId) {
            this.setState({ historyRows: [], historyLoading: false });
            return;
        }
        this.setState({ historyLoading: true });
        fetchResultHistory(patientId, testId, data._id)
            .then((response) => {
                const rows = response && Array.isArray(response.data) ? response.data.slice(0, 3) : [];
                this.setState({ historyRows: rows, historyLoading: false });
            })
            .catch(() => {
                this.setState({ historyRows: [], historyLoading: false });
            });
    };

    formatHistoryDate = (value) => {
        if (!value) return 'N/A';
        const str = value.toString();
        // results.released is a 'YYYYMMDDHHmmss' string; updatedAt is an ISO date
        if (/^\d{14}$/.test(str)) {
            return `${str.slice(4, 6)}/${str.slice(6, 8)}/${str.slice(0, 4)}`;
        }
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? str : parsed.toLocaleDateString();
    };

    render() {
        const { hasResults, resultStatus, statusColor, pdfPath, abnormalCount, inconsistentCount } = this.state;
        const hasAbnormal = abnormalCount > 0;
        const hasInconsistent = inconsistentCount > 0;
        const isClickable = hasResults && this.state.rowData.length > 0;

        let requisitionNumber = '';

        if (this.props.data?.parent_id && this.props.data?.parent_id.parent_id) {
            requisitionNumber = this.props.data?.parent_id.parent_id.lab_order_id;
        } else {
            requisitionNumber = this.props.data?.parent_id?.lab_order_id;
        }
        return (
            <div>
                <div
                    style={{
                        color: statusColor,
                        fontWeight: hasResults ? '500' : 'normal',
                        cursor: isClickable ? 'pointer' : 'default',
                        textDecoration: isClickable ? 'underline' : 'none'
                    }}
                    onClick={isClickable ? this.handleClick : undefined}
                    title={isClickable ? 'Click to view detailed results' : ''}
                >
                    {(hasInconsistent || hasAbnormal) && <span style={{ marginRight: '5px' }}>⚠️</span>}
                    {resultStatus}
                    {pdfPath && (
                        <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.7 }}>
                            <i className="fa fa-file-pdf-o"></i>
                        </span>
                    )}
                </div>

                <ModalStyled
                    size="lg"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                    show={this.state.showPopup}
                    onHide={this.handleClose}
                    backdrop="static"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Test Results - {this.props.data?.test_id?.name || 'Unknown Test'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row form-row">
                            <div className="col-12 mb-3">
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Patient:</strong> {this.props.data?.patient_id?.first_name}{' '}
                                        {this.props.data?.patient_id?.last_name}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Sample ID:</strong>{' '}
                                        {this.props.data?.sample_id || this.props.data?.test_info?.sample || 'N/A'}
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <strong>Requisition: </strong>
                                        {requisitionNumber || 'N/A'}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Status:</strong>{' '}
                                        <span style={{ color: statusColor }}>{resultStatus}</span>
                                    </div>
                                </div>
                                {this.state.totalCount > 0 && (
                                    <div className="row mt-2">
                                        <div className="col-12">
                                            <strong>Summary:</strong> {this.state.totalCount} analytes tested
                                            {hasInconsistent && ` (${this.state.inconsistentCount} inconsistent)`}
                                            {!hasInconsistent &&
                                                hasAbnormal &&
                                                ` (${this.state.abnormalCount} abnormal)`}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <ThemeContext.Consumer>
                                {({ themeName }) => (
                                    <div
                                        id="analyteResultsGrid"
                                        style={{
                                            height: '400px',
                                            width: '100%',
                                            marginBottom: '20px'
                                        }}
                                    >
                                        <AgGridReact
                                            columnDefs={this.state.columnDefs}
                                            defaultColDef={this.state.defaultColDef}
                                            rowData={this.state.rowData}
                                            pagination={true}
                                            paginationPageSize={20}
                                            paginationNumberFormatter={this.state.paginationNumberFormatter}
                                            theme={themeName === 'Light' ? themeLight : themeDark}
                                            quickFilterText={this.state.quickFilterText}
                                        />
                                    </div>
                                )}
                            </ThemeContext.Consumer>
                            {this.state.showHistory && (
                                <div
                                    className="col-12"
                                    style={{
                                        paddingTop: '12px',
                                        marginBottom: '20px',
                                        borderTop: '1px solid rgba(0,0,0,0.2)'
                                    }}
                                >
                                    <div style={{ marginBottom: '8px' }}>
                                        <strong>History</strong>{' '}
                                        <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
                                            (previous results for this patient &amp; test)
                                        </span>
                                    </div>
                                    {this.state.historyLoading ? (
                                        <div>Loading...</div>
                                    ) : this.state.historyRows.length === 0 ? (
                                        <div style={{ opacity: 0.7 }}>No prior results</div>
                                    ) : (
                                        <table className="table table-sm" style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Result</th>
                                                    <th>Order #</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.historyRows.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td>{this.formatHistoryDate(row.date)}</td>
                                                        <td>{row.resultValue || 'N/A'}</td>
                                                        <td>{row.orderNumber || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                            <div
                                className="col-12"
                                style={{
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(0,0,0,0.2)'
                                }}
                            >
                                {pdfPath && (
                                    <Button
                                        variant="primary"
                                        style={{ float: 'left' }}
                                        onClick={() => window.open(pdfPath, '_blank')}
                                    >
                                        <i className="fa fa-file-pdf-o"></i> View PDF
                                    </Button>
                                )}
                                <Button variant="secondary" style={{ float: 'right' }} onClick={this.handleClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </Modal.Body>
                </ModalStyled>
            </div>
        );
    }
}
